<template>
    <article>
        <p>
            Looking through endless postmortem reports and talking to other SREs,
            I feel that about 80% of outages are caused by similar factors: infrastructure failures, network errors/delays, lack of computing resources, etc.
            However, the cause of the remaining 20% can vary significantly since not all applications are built the same way.
        </p>

        <h2>Logs</h2>

        <p>
            All well-designed applications log their errors, so using a log management system can help you navigate through the logs quite efficiently.
            Yet, the cost of such systems can be unreasonably high.
            In fact, engineers don't even need all the logs when investigating an incident, because it's impossible
            to read every single message in a short period of time.
            Instead, they try extracting some sort of summary from the logs related to a specific timespan.
            If using console utilities, it should look like this:
        </p>

        <ul>
            <li>
                Getting the stream of the related log:
                <pre v-hl.sh>
&lt; cat | tail | journalctl | kubectl logs &gt; ...
                </pre>
            </li>
            <li>
                Identifying the format to filter messages by severity:
                <pre v-hl.sh>
... | grep ERROR
                </pre>
            </li>
            <li>
                If needed, doing something with multi-line messages:
                <pre v-hl.sh>
... | grep -C&lt;N&gt; ...
                </pre>
            </li>

            <li>
                Clustering (grouping) messages to identify the most frequent errors:
                <pre v-hl.sh>
... |  &lt; some awk | sed | sort magic &gt;
                </pre>
            </li>
        </ul>

        <p>
            The resulting summary is usually a list of message groups with a sample and the number of occurrences.
        </p>

        <p>
            It seems like this particular type of analysis can be done without a centralized log management system.
            Moreover, with some automation, we can greatly speed up this type of investigation.
        </p>

        <p>
            At Coroot, we implemented an automated log parsing in our open-source Prometheus exporter <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent</a>.
            To explain how it works, let's follow the same steps as I mentioned above.
        </p>

        <h2>Container logs discovery</h2>

        <p>
            The agent can discover the logs of each container running on a node by using different data sources depending on the container type:
        </p>

        <ul>
            <li>
                A process might log directly to a file in <var>/var/log</var>. In this case, the agent detects this by reading the list of open files of each process from <var>/proc/&lt;pid&gt;/fd</var>.
            </li>
            <li>
                Services managed by <var>systemd</var> usually use <var>journald</var> as a logging backend, so the agent can read a container's log from <var>journald</var>.
            </li>
            <li>
                To find logs of Kubernetes pods or standalone containers, the agent uses meta-information from <var>dockerd</var> or <var>containerd</var>.
            </li>
        </ul>

        <h2>Handling multi-line messages</h2>

        <p>
            To extract multi-line messages from a log stream, we use a few dead-simple heuristics.
            The general one: if a line contains a field that looks like a datetime, and the following lines don't &mdash; these are probably parts of a multi-line message.
            In addition to that, the agent uses some special <a href="https://github.com/coroot/logparser/blob/d998966a74541e3fde1a69fde7801bc2e58317c8/multiline.go#L101" target="_blank" rel="noopener noreferrer">parsing rules</a> for Java stack traces and Python tracebacks.
        </p>

        <p>
            According to our tests, this simple approach works pretty accurately and it can even handle custom multi-line messages.
        </p>

        <h2>Severity level detection</h2>

        <p>
            I would say it's not actually detection, it's more like guessing.
            For most log formats, the agent simply looks for a mention of known levels at the beginning of each message.
            In addition, we implemented special parsers for
                <a href="https://github.com/kubernetes/klog" target="_blank" rel="noopener noreferrer">glog/klog</a> and
                <a href="https://github.com/coroot/logparser/blob/d998966a74541e3fde1a69fde7801bc2e58317c8/level.go#L112" target="_blank" rel="noopener noreferrer">redis</a> log formats.
            If it is impossible to detect the level of severity, the <var>UNKNOWN</var> level is used.
        </p>

        <h2>Messages clustering</h2>

        <p>
            A great compilation of the publications related to automated log parsing is available <a href="https://github.com/logpai/logparser#log-parsers-currently-available" target="_blank" rel="noopener noreferrer">here</a>.
            After reviewing these papers, we realized that our problem is much simpler than the ones most researchers have tried to solve.
            We just want to cluster messages to groups identified by some fingerprint.
            In doing so, we do not need to recognize the message format itself.
        </p>

        <p>
            Our approach is entirely empirical and based on the observation that a logging entity is actually a combination of a template and variables,
            such as date, time, severity level, component, and other user-defined variables.
            We discovered that after removing everything that looks like a variable from a message,
            the remaining part itself can be considered as the pattern of this message.
        </p>

        <p>
            In the first step, the agent removes quoted parts, parts in brackets, HEX numbers, UUIDs, and numeric values.
            Then, the remaining words are used to calculate the message group fingerprint.
            We extracted the code related to log parsing to this separate <a href="https://github.com/coroot/logparser" target="_blank" rel="noopener noreferrer">repository</a> along with a command-line tool that can parse a log stream from <var>stdin</var>.
        </p>

        <p>
            Here is a summary for a log from the <a href="https://github.com/logpai/loghub" target="_blank" rel="noopener noreferrer">logpai/loghub</a> dataset (kudos to the Logpai team for sharing this dataset):
        </p>

        <pre v-hl.sh>
cat Zookeeper_2k.log | docker run -i --rm ghcr.io/coroot/logparser
        </pre>

        <pre v-hl>
<span class="log-error">▇                     </span> 12 ( 0%) - ERROR [LearnerHandler-/10.10.34.11:52225:LearnerHandler@562] - Unexpected exception causing shutdown while sock still open
<span class="log-error">▇                      </span> 1 ( 0%) - ERROR [CommitProcessor:1:NIOServerCnxn@180] - Unexpected Exception:
<span class="log-warning">▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇</span> 314 (23%) - WARN [SendWorker:188978561024:QuorumCnxManager$SendWorker@679] - Interrupted while waiting for message on queue
<span class="log-warning">▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇  </span> 291 (21%) - WARN [RecvWorker:188978561024:QuorumCnxManager$RecvWorker@762] - Connection broken for id 188978561024, my id = 1, error =
<span class="log-warning">▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇    </span> 266 (19%) - WARN [RecvWorker:188978561024:QuorumCnxManager$RecvWorker@765] - Interrupting SendWorker
<span class="log-warning">▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇▇    </span> 262 (19%) - WARN [SendWorker:188978561024:QuorumCnxManager$SendWorker@688] - Send worker leaving thread
<span class="log-warning">▇▇▇▇▇▇                </span> 86 ( 6%) - WARN [WorkerSender[myid=1]:QuorumCnxManager@368] - Cannot open channel to 2 at election address /10.10.34.12:3888
<span class="log-warning">▇▇▇                   </span> 39 ( 2%) - WARN [NIOServerCxn.Factory:0.0.0.0/0.0.0.0:2181:ZooKeeperServer@793] - Connection request from old client /10.10.34.19:33442; will be dropped if server is in r-o mode
<span class="log-warning">▇▇▇                   </span> 37 ( 2%) - WARN [NIOServerCxn.Factory:0.0.0.0/0.0.0.0:2181:NIOServerCnxn@349] - caught end of stream exception
<span class="log-warning">▇▇                    </span> 19 ( 1%) - WARN [LearnerHandler-/10.10.34.12:35276:LearnerHandler@575] - ******* GOODBYE /10.10.34.12:35276 ********
<span class="log-warning">▇                      </span> 3 ( 0%) - WARN [NIOServerCxn.Factory:0.0.0.0/0.0.0.0:2181:NIOServerCnxn@354] - Exception causing close of session 0x0 due to java.io.IOException: ZooKeeperServer not running
<span class="log-warning">▇                      </span> 1 ( 0%) - WARN [LearnerHandler-/10.10.34.13:42241:Leader@576] - First is 0x0

1996 messages processed in 0.053 seconds:
  error: 13
  warning: 1318
  info: 665
</pre>


        <h2>Metrics</h2>

        Node-agent parses the logs of every container running on a node and exports the
        <MetricLink name="container_log_messages_total" exporter="node-agent"/>:

        <pre v-hl.sh>
container_log_messages_total{
    container_id="&lt;ID of the container writing this log&gt;",
    source="&lt;log path or the stream source&gt;",
    severity="&lt;DEBUG|INFO|WARNING|ERROR|FATAL|UNKNOWN&gt;",
    pattern_hash="&lt;hash of the recognized pattern&gt;",
    sample="&lt;a sample message that is matched to this pattern&gt;", # this can be replaced with Prometheus examplars in the future
}
        </pre>


        <p>
            <a href="https://github.com/coroot/coroot-aws-agent" target="_blank" rel="noopener noreferrer">AWS-agent</a> exports the similar metrics
            <MetricLink name="aws_rds_log_messages_total" exporter="aws-agent"/> related to every discovered RDS instance.
        </p>


        <p>
            After Prometheus has collected the metrics from the node-agents, you can query these metrics using PromQL expressions like this:
        </p>

        <pre v-hl.sh>
sum by(level) (rate(container_log_messages_total{container_id="/docker/prometheus"}[1m]))
        </pre>


        <h2>How we use these metrics</h2>

        <p>
            Coroot's Logs Inspection (part of our commercial product) uses the log-based metrics to highlight errors that are correlating with the application SLIs.
            Here is a sample report:
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/mining-metrics-from-unstructured-logs/logs-report.png" />
        </v-card>

        <p>
            For each pattern, Coroot provides a full-text sample and a timeline of events broken down by the app's instances.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/mining-metrics-from-unstructured-logs/logs-report-drilldown.png" />
        </v-card>

        <h2>Useful links</h2>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Get started with Coroot</router-link>
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
            <li>
                <a href="https://github.com/coroot/logparser" target="_blank" rel="noopener noreferrer">logparser <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'inspections', item: 'logs'}}">Logs Inspection <v-icon dense>mdi-help-circle-outline</v-icon></router-link>
            </li>
        </ul>


    </article>
</template>

<script>
import MetricLink from "@/components/MetricLink";

export default {
    components: {MetricLink}
}
</script>

<style scoped>
.log-error {
    color: red;
}
.log-warning {
    color: yellow;
}
</style>
